import * as React from "react"
import { createPortal } from "react-dom"
import { CheckCircle2, XCircle, AlertTriangle, Info, Loader2, X } from "lucide-react"
import { useFullscreenHost } from "@vc/viewmodels/screen/useFullscreenHost"
import { cn } from "@vc/lib/utils"
import { useTranslation } from "@/clientWeb/i18n/LanguageContext"
import "@/clientWeb/components/ui/toastAnimation.css"
import { useMeetingNoticesStore } from "../store/meeting-notices.store"
import type { MeetingNotice, NoticeVariant } from "../services/notice-stack"

const variantIcon: Record<NoticeVariant, { icon: React.ElementType; iconClass: string; borderClass: string; progressClass: string }> = {
  success: { icon: CheckCircle2, iconClass: "text-emerald-500", borderClass: "border-l-emerald-500", progressClass: "bg-emerald-500" },
  error: { icon: XCircle, iconClass: "text-red-500", borderClass: "border-l-red-500", progressClass: "bg-red-500" },
  warning: { icon: AlertTriangle, iconClass: "text-amber-500", borderClass: "border-l-amber-500", progressClass: "bg-amber-500" },
  info: { icon: Info, iconClass: "text-blue-500", borderClass: "border-l-blue-500", progressClass: "bg-blue-500" },
  pending: { icon: Loader2, iconClass: "text-violet-500 animate-spin", borderClass: "border-l-violet-500", progressClass: "bg-violet-500" },
  default: { icon: Info, iconClass: "text-muted-foreground", borderClass: "border-l-border", progressClass: "bg-muted-foreground" },
}

function OrdinaryNoticeTile({
  notice,
  showClearAll,
  clearAllLabel,
  closeLabel,
  onDismiss,
  onClearAll,
}: {
  notice: MeetingNotice
  showClearAll: boolean
  clearAllLabel: string
  closeLabel: string
  onDismiss: (id: string | number) => void
  onClearAll: () => void
}) {
  const { icon: Icon, iconClass, borderClass, progressClass } = variantIcon[notice.variant] ?? variantIcon.default

  // Runs again whenever this id is updated in place (a new duration means a
  // fresh timer), and is skipped entirely for a duration of Infinity: that
  // notice waits for an explicit dismiss or a later update, same as the
  // sonner-backed toast it replaces.
  React.useEffect(() => {
    if (!Number.isFinite(notice.duration)) return
    const timer = setTimeout(() => onDismiss(notice.id), notice.duration)
    return () => clearTimeout(timer)
  }, [notice.id, notice.duration, onDismiss])

  return (
    <div
      className={cn(
        "relative flex items-start gap-3 bg-card border border-border border-l-[3px] rounded-xl shadow-lg px-4 py-3.5 w-full overflow-hidden",
        borderClass,
      )}
    >
      <Icon className={cn("w-4 h-4 shrink-0 mt-0.5", iconClass)} />
      <div className="flex-1 min-w-0">
        <p className="text-sm font-semibold text-foreground leading-tight">{notice.title}</p>
        {notice.description && (
          <p className="text-xs text-muted-foreground mt-0.5 leading-snug">{notice.description}</p>
        )}
        {notice.action && (
          <button
            onClick={() => {
              notice.action?.onClick()
              onDismiss(notice.id)
            }}
            className="mt-2 text-xs font-semibold text-primary hover:underline"
          >
            {notice.action.label}
          </button>
        )}
      </div>
      <div className="shrink-0 flex items-center gap-1 mt-0.5">
        {showClearAll && (
          <button
            onClick={onClearAll}
            className="px-1.5 py-0.5 rounded-md text-[11px] font-semibold text-primary hover:bg-muted transition-colors whitespace-nowrap"
          >
            {clearAllLabel}
          </button>
        )}
        <button
          onClick={() => onDismiss(notice.id)}
          aria-label={closeLabel}
          className="p-0.5 rounded-md text-muted-foreground hover:text-foreground hover:bg-muted transition-colors"
        >
          <X className="w-3.5 h-3.5" />
        </button>
      </div>
      <div className="absolute bottom-0 left-0 right-0 h-[2px] bg-border/40">
        <div
          className={cn("h-full w-full origin-right", progressClass)}
          style={{ animation: `toast-progress ${notice.duration}ms linear forwards` }}
        />
      </div>
    </div>
  )
}

/**
 * The meeting's notices, stacked above the control bar instead of sitting on
 * top of it: newest on top, at most three (the oldest goes first), each one
 * closing on its own timer or by hand. `toast-utils.tsx`'s sink is what fills
 * the store while a meeting is on screen, so every existing `toast.*` call
 * lands here unchanged.
 *
 * An earlier version of this file also carried an inline moderator "requests
 * card" (media/presenter/annotation/lobby asks, pinned at the bottom of the
 * stack). Dropped in the saireddy_dev merge in favor of PresenterRequestDialog
 * for presenter requests specifically; lobby knocks are still reviewable from
 * ParticipantsPanel. Annotation asks are answered in PresenterRequestDialog too
 * (subject "annotation"), after any presenter ask.
 */
export function MeetingNoticeStack() {
  const host = useFullscreenHost()
  const t = useTranslation()

  const ordinary = useMeetingNoticesStore((s) => s.ordinary)
  const dismissOrdinary = useMeetingNoticesStore((s) => s.dismissOrdinary)
  const clearOrdinary = useMeetingNoticesStore((s) => s.clearOrdinary)

  // In the video area, not the window: at its bottom right. A side panel
  // narrows the video area, so the notices move left with the videos instead
  // of covering the panel, and on phones the video area already ends above
  // the control bar. Above the tiles, under the side-panel overlay, menus,
  // sheets and dialogs. In full screen they are portalled to the full-screen
  // element and pinned to the screen instead.
  const inFullscreen = !!host && host !== document.body

  const container = (
    <div
      className={cn(
        inFullscreen ? "fixed z-[45]" : "absolute z-[25]",
        "right-3 bottom-3 flex flex-col gap-2 w-[320px] max-w-[calc(100%-1.5rem)] pointer-events-none",
      )}
    >
      {ordinary.map((notice) => (
        <div key={notice.id} className="pointer-events-auto">
          <OrdinaryNoticeTile
            notice={notice}
            showClearAll={ordinary.length > 1 && ordinary[0].id === notice.id}
            clearAllLabel={`${t("panels.clearAll")} (${ordinary.length})`}
            closeLabel={t("common.close") || "Close"}
            onDismiss={dismissOrdinary}
            onClearAll={clearOrdinary}
          />
        </div>
      ))}
    </div>
  )

  // Same reasoning as the chat toast container: a shared screen or a
  // maximised tile holds the whole fullscreen element, and a notice left in
  // the normal tree would be painted behind it, never seen.
  if (!inFullscreen) return container
  return createPortal(container, host)
}
